import React from 'react';
import { Box, CircularProgress, Icon, Chip } from '@mui/material';
import MDTypography from 'src/components/md/MDTypography';
import useProductVariant from 'src/api/inventory-management/products/useProductVariant';

interface SelectedVariantSummaryProps {
  variantId: string;
}

/**
 * Component that displays the currently selected product variant
 * with a prominent visual indicator showing it's the active selection
 */
const SelectedVariantSummary: React.FC<SelectedVariantSummaryProps> = ({
  variantId,
}) => {
  const { data, isLoading, error } = useProductVariant(variantId, {
    enabled: !!variantId,
    includeInactive: true,
  });
  const productVariant = data?.productVariant;
  const attributeDescriptions = data?.attributeDescriptions || [];
  const hasAttributes = attributeDescriptions.length > 0;
  const isInactive = productVariant?.active === false;

  if (isLoading) {
    return (
      <Box
        mt={2}
        p={2}
        border="1px solid"
        borderColor="rgba(0, 164, 164, 0.3)"
        borderRadius="borderRadius"
        bgcolor="rgba(0, 164, 164, 0.05)"
        display="flex"
        alignItems="center"
      >
        <CircularProgress size={24} sx={{ mr: 2, color: '#00a4a4' }} />
        <MDTypography variant="body2" color="#00a4a4">
          Loading selected variant information...
        </MDTypography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        mt={2}
        p={2}
        border="1px solid"
        borderColor="rgba(211, 47, 47, 0.3)"
        borderRadius="borderRadius"
        bgcolor="rgba(211, 47, 47, 0.05)"
        display="flex"
        alignItems="center"
      >
        <Icon sx={{ mr: 2, color: 'error.main' }}>error_outline</Icon>
        <MDTypography variant="body2" color="error.main">
          {error.message || 'Error loading selected variant'}
        </MDTypography>
      </Box>
    );
  }

  if (!productVariant) {
    return null;
  }

  return (
    <Box
      mt={2}
      p={2}
      border="1px solid"
      borderColor={
        isInactive ? 'rgba(239, 68, 68, 0.4)' : 'rgba(0, 164, 164, 0.3)'
      }
      borderRadius="borderRadius"
      bgcolor={
        isInactive ? 'rgba(239, 68, 68, 0.05)' : 'rgba(0, 164, 164, 0.05)'
      }
    >
      {/* Header section with clear indication this is currently selected */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={1}
      >
        <Box display="flex" alignItems="center">
          <Icon sx={{ color: isInactive ? '#ef4444' : '#00a4a4', mr: 1.5 }}>
            {isInactive ? 'warning' : 'check_circle'}
          </Icon>
          <MDTypography
            variant="caption"
            fontWeight="medium"
            color={isInactive ? '#ef4444' : '#00a4a4'}
            textTransform="uppercase"
            letterSpacing={0.5}
          >
            {isInactive
              ? 'Inactive Variant - Must Be Changed'
              : 'Currently Selected Product Variant'}
          </MDTypography>
        </Box>
      </Box>

      {/* Product variant details */}
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            <Box>
              <MDTypography
                variant="subtitle2"
                fontWeight="medium"
                color={isInactive ? '#ef4444' : '#00a4a4'}
              >
                {(productVariant.originMetadata?.NAMEINVC as string) ||
                  productVariant.sku ||
                  'Selected Variant'}
              </MDTypography>
              {productVariant.sku && (
                <MDTypography variant="caption" color="text.secondary">
                  SKU: {productVariant.sku}
                </MDTypography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Display attributes if available */}
        {hasAttributes && (
          <Box
            display="flex"
            flexWrap="wrap"
            gap={0.75}
            mt={1.5}
            sx={{ maxWidth: '100%' }}
          >
            {attributeDescriptions.map((attr, index) => (
              <Chip
                key={index}
                label={
                  <Box component="span">
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 500,
                        opacity: 0.9,
                      }}
                    >
                      {attr.name}:
                    </Box>{' '}
                    <Box component="span" sx={{ opacity: 0.8 }}>
                      {attr.value}
                    </Box>
                  </Box>
                }
                size="small"
                variant="outlined"
                sx={{
                  height: '22px',
                  fontSize: '0.7rem',
                  borderColor: isInactive
                    ? 'rgba(239, 68, 68, 0.4)'
                    : 'rgba(0, 164, 164, 0.4)',
                  color: isInactive ? '#dc2626' : '#006666',
                  backgroundColor: isInactive
                    ? 'rgba(239, 68, 68, 0.08)'
                    : 'rgba(0, 164, 164, 0.08)',
                  '& .MuiChip-label': {
                    px: 1,
                    py: 0,
                  },
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SelectedVariantSummary;
